import { useRouter } from "@core/Api/useRouter";
import Path from "@core/FileProvider/Path/Path";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import useGetArticleContextData from "@core-ui/ScopedContextWrapper/useGetArticleContextData";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { setOnlyChangesEnabled } from "@ext/git/core/Diff/components/store/DiffOnlyChangesStore";
import alignResourceHrefs from "@ext/git/core/Diff/logic/alignResourceHrefs";
import { getReactNodeRenderers } from "@ext/git/core/Diff/logic/getReactNodeRenderers";
import useDiff from "@ext/git/core/Diff/logic/hooks/useDiff";
import { useDiffStyles } from "@ext/git/core/Diff/logic/hooks/useDiffStyles";
import { disableLiveDiff, parseScope, useLiveDiffStore } from "@ext/git/core/Diff/logic/live/liveDiffStore";
import { useLiveDiffBinding } from "@ext/git/core/Diff/logic/live/useLiveDiffBinding";
import { onlyChangesKey, setOnlyChanges } from "@ext/git/core/Diff/logic/onlyChanges";
import { scrollableParent } from "@ext/git/core/Diff/logic/scrollToDiff";
import type { DiffOptions } from "@gaurussel/tiptap-diff-utility";
import type { JSONContent } from "@tiptap/core";
import { EditorState } from "@tiptap/pm/state";
import type { Editor } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface RevisionContext {
	apiUrlCreator: ReturnType<typeof useGetArticleContextData>["apiUrlCreator"];
	isLoading: boolean;
}

interface LiveDiffProps {
	editor: Editor;
	articlePath: string;
	updateDiffOptions: (patch: Partial<DiffOptions>) => void;
	flushUpdate: () => Promise<void>;
	setForeignContent: (foreign: boolean) => void;
	revisionContext?: RevisionContext;
}

const shutdown = (editor: Editor, updateDiffOptions: LiveDiffProps["updateDiffOptions"]) => {
	if (editor.isDestroyed) return;
	updateDiffOptions({ enabled: false, baseline: undefined });
	editor.commands.setDiffEnabled(false);
	setOnlyChanges(editor.view, false);
	setOnlyChangesEnabled(false);
	editor.commands.updateDiffLinesModel([]);
	editor.commands.updateMovedMarkers([]);
	editor.commands.updateDiffChanges([]);
	editor.view.dispatch(editor.state.tr.setMeta(onlyChangesKey, { type: "model", stops: [], keepVisible: [] }));
};

const touch = (editor: Editor) =>
	editor.view.dispatch(editor.state.tr.setMeta("preventUpdate", true).setMeta("addToHistory", false));

const scrollTop = (editor: Editor) => scrollableParent(editor.view.dom as HTMLElement)?.scrollTop ?? 0;
const setScrollTop = (editor: Editor, top: number) => {
	const container = scrollableParent(editor.view.dom as HTMLElement);
	if (container) container.scrollTop = top;
};

// Must render inside CommentEditorProvider, otherwise useDiff gets no comment events and never runs.
// Revisions swap the whole EditorState: replacing the doc via a transaction wipes undo history, even with addToHistory: false.
const LiveDiff = ({
	editor,
	articlePath,
	updateDiffOptions,
	flushUpdate,
	setForeignContent,
	revisionContext,
}: LiveDiffProps) => {
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const apiUrlCreator = ApiUrlCreatorService.value;
	const router = useRouter();

	const { session, on } = useLiveDiffBinding({ articlePath, view: "wysiwyg" });
	useDiffStyles(on);

	const filePath = session?.data?.sideBarData?.data?.filePath;
	const oldArticlePath = on && catalogName ? Path.join(catalogName, filePath?.oldPath ?? filePath?.path) : null;
	const oldScope = useMemo(
		() => (on ? (parseScope(session.oldScope) ?? "HEAD") : undefined),
		[on, session?.oldScope],
	);
	const newScope = useMemo(() => (on ? parseScope(session.scope) : undefined), [on, session?.scope]);

	const {
		apiUrlCreator: oldApiUrlCreator,
		catalogProps: oldCatalogProps,
		isLoading: isOldContextLoading,
	} = useGetArticleContextData({
		articlePath: oldArticlePath,
		catalogName: on ? catalogName : null,
		scope: oldScope,
	});

	const revisionTree: JSONContent | null = on && session.revision ? session.revision.editTree : null;
	const sessionId = session?.id;
	// Saved state belongs to one editor instance; never restore it into a recreated editor.
	const savedState = useRef<{ editor: Editor; state: EditorState; scroll: number } | null>(null);
	const [swapped, setSwapped] = useState(false);
	const [swapping, setSwapping] = useState(false);
	// Keeps a stale async swap from overwriting the state of a newer one.
	const transitions = useRef(0);

	const releaseEditor = useCallback(() => {
		if (editor.isDestroyed) return;
		editor.setEditable(true, false);
		setForeignContent(false);
	}, [editor, setForeignContent]);

	const restore = useCallback(() => {
		const saved = savedState.current;
		savedState.current = null;
		setSwapped(false);
		if (!saved || editor.isDestroyed) return;
		if (saved.editor === editor) {
			editor.view.updateState(saved.state);
			touch(editor);
			setScrollTop(editor, saved.scroll);
		}
		releaseEditor();
		if (!useLiveDiffStore.getState().session) shutdown(editor, updateDiffOptions);
	}, [editor, releaseEditor, updateDiffOptions]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: swap is keyed by revision tree and session id; the rest is read at call time
	useEffect(() => {
		if (editor.isDestroyed) return;
		const transition = ++transitions.current;
		const mine = () => transitions.current === transition;
		if (!revisionTree) {
			restore();
			setSwapping(false);
			return;
		}
		if (savedState.current && savedState.current.editor !== editor) {
			savedState.current = null;
			setSwapped(false);
		}
		setSwapping(true);
		const run = async () => {
			if (!savedState.current) {
				// Lock input before flushing, or an edit during the wait schedules a new save debounce.
				editor.setEditable(false, false);
				setForeignContent(true);
				let failed = false;
				try {
					await flushUpdate();
				} catch {
					failed = true;
				}
				const current = useLiveDiffStore.getState().session;
				const ours = !editor.isDestroyed && current?.id === sessionId && current.status === "on";
				if (failed && ours) {
					releaseEditor();
					disableLiveDiff(router);
					if (mine()) setSwapping(false);
					return;
				}
				if (!mine() || !ours) {
					if (!current?.revision) releaseEditor();
					if (mine()) setSwapping(false);
					return;
				}
				savedState.current = { editor, state: editor.state, scroll: scrollTop(editor) };
			}
			const doc = editor.schema.nodeFromJSON(revisionTree);
			editor.view.updateState(EditorState.create({ doc, plugins: editor.state.plugins }));
			touch(editor);
			setSwapped(true);
			setSwapping(false);
		};
		void run();
	}, [editor, revisionTree, sessionId]);

	const revisionReady = !revisionTree ? !swapped : swapped && !revisionContext?.isLoading;
	const ready = on && !!session.baseline?.editTree && !isOldContextLoading && !swapping && revisionReady;
	const oldContext = useMemo(
		() => (ready ? { apiUrlCreator: oldApiUrlCreator, catalogProps: oldCatalogProps } : undefined),
		[ready, oldApiUrlCreator, oldCatalogProps],
	);
	const rawBaselineTree = ready ? session.baseline.editTree : null;
	const baselineTree = useMemo(
		() => (rawBaselineTree ? alignResourceHrefs(rawBaselineTree, editor) : null),
		[rawBaselineTree, editor],
	);
	const newApiUrlCreator = revisionTree && revisionContext ? revisionContext.apiUrlCreator : apiUrlCreator;

	// biome-ignore lint/correctness/useExhaustiveDependencies: baseline changes go through a separate effect without disabling
	useEffect(() => {
		if (!ready || editor.isDestroyed) return;
		editor.commands.updateDiffContext({ articlePath: oldArticlePath, oldScope, newScope: newScope ?? null });
		updateDiffOptions({ enabled: true, baseline: baselineTree, react: getReactNodeRenderers({ oldContext }) });
		return () => shutdown(editor, updateDiffOptions);
	}, [ready, editor, oldContext, oldArticlePath, oldScope, newScope, updateDiffOptions]);

	useEffect(() => {
		if (!ready || editor.isDestroyed) return;
		updateDiffOptions({ baseline: baselineTree });
	}, [ready, editor, baselineTree, updateDiffOptions]);

	const baselineDoc = useMemo(
		() => (baselineTree ? editor.schema.nodeFromJSON(baselineTree) : null),
		[baselineTree, editor],
	);
	const baseline = useCallback(() => baselineDoc, [baselineDoc]);

	useDiff(
		ready
			? { newEditor: editor, baseline, newApiUrlCreator, oldApiUrlCreator }
			: { newEditor: null, baseline: null, newApiUrlCreator: null, oldApiUrlCreator: null },
	);

	return null;
};

export default LiveDiff;
